import React, { useState, useEffect } from 'react';
import { Link } from 'wouter';
import {
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import { GlobalBanner } from '@/components/notifications';
import { Button } from '@/components/ui/button';

interface IncidentBannerProps {
  className?: string;
}

interface Incident {
  id: string;
  title: string;
  severity: 'degraded' | 'major' | 'critical';
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  message: string;
  affectedServices: string[];
  updatedAt: string;
  statusPageUrl?: string;
}

export default function IncidentBanner({ className }: IncidentBannerProps) {
  const [activeIncident, setActiveIncident] = useState<Incident | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  // Mock function to fetch current incidents - in real app this would call an API
  const fetchActiveIncident = (): Incident | null => {
    // Disabled - no incidents shown per user request
    return null;
  };

  // Check for incidents on mount and periodically
  useEffect(() => {
    const checkIncidents = () => {
      const incident = fetchActiveIncident();
      setActiveIncident(incident);

      // Auto-dismiss if incident is resolved
      if (incident?.status === 'resolved') {
        setTimeout(() => {
          setActiveIncident(null);
          setIsDismissed(false);
        }, 3000);
      }
    };

    checkIncidents();

    // Check every 30 seconds for incident updates
    const interval = setInterval(checkIncidents, 30000);

    return () => clearInterval(interval);
  }, []);

  if (!activeIncident || isDismissed) {
    return null;
  }

  return (
    <GlobalBanner
      className={className}
      tone={activeIncident.severity === 'critical' ? 'critical' : 'warning'}
      icon={<AlertTriangle className="h-4 w-4" />}
      title={
        <div className="flex items-center gap-2">
          <span>{activeIncident.title}</span>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
            {activeIncident.status}
          </span>
        </div>
      }
      description={
        <div className="space-y-1 text-sm">
          <p>{activeIncident.message}</p>
          {activeIncident.affectedServices.length > 0 && (
            <p className="text-xs text-slate-600">
              Affected: {activeIncident.affectedServices.join(', ')}
            </p>
          )}
        </div>
      }
      actions={
        activeIncident.statusPageUrl ? (
          <Link href={activeIncident.statusPageUrl}>
            <Button
              variant="outline"
              size="sm"
              data-testid="button-status-page"
            >
              Status Page
              <ExternalLink className="h-3 w-3 ml-1" />
            </Button>
          </Link>
        ) : null
      }
      onDismiss={() => setIsDismissed(true)}
      dismissLabel="Dismiss"
      data-testid="incident-banner"
    />
  );
}
